import { ComponentProps, ReactNode, useMemo } from "react";
import { useSecureFileUrl } from "@tellescope/tanstack-query";
import { Button, cn, Dialog, DialogContent, DialogTitle, DialogTrigger } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Download, FileText } from "lucide-react";

export interface FilePreviewDialogProps extends Omit<ComponentProps<typeof Dialog>, "children"> {
  children?: ReactNode;
  fileName?: string;
  fileType: string;
  secureName: string;
}

export const FilePreviewDialog = ({
  children,
  secureName,
  fileName,
  fileType,
  ...dialogProps
}: FilePreviewDialogProps) => {
  const { t } = useTranslation();
  const src = useSecureFileUrl(secureName);

  const { fileIconColor, canPreview, userFriendlyType } = useMemo<{
    canPreview: boolean;
    fileIconColor: string;
    userFriendlyType: string;
  }>(() => {
    const lowerFileType = fileType.toLowerCase();
    if (lowerFileType.includes("pdf")) {
      return { canPreview: false, fileIconColor: "text-black", userFriendlyType: t("PDF Document") };
    } else if (lowerFileType.includes("doc")) {
      return { canPreview: false, fileIconColor: "text-blue-600", userFriendlyType: t("Word Document") };
    } else if (lowerFileType.includes("png") || lowerFileType.includes("jpg") || lowerFileType.includes("jpeg")) {
      return { canPreview: true, fileIconColor: "text-green-600", userFriendlyType: t("Image") };
    } else {
      return {
        canPreview: false,
        fileIconColor: "text-gray-600",
        userFriendlyType: lowerFileType.includes("/")
          ? (lowerFileType.split("/")[1] ?? lowerFileType).toUpperCase()
          : lowerFileType.toUpperCase(),
      };
    }
  }, [fileType, t]);

  return (
    <Dialog {...dialogProps}>
      {!!children && <DialogTrigger>{children}</DialogTrigger>}
      <DialogContent>
        <DialogTitle className="truncate max-w-[90%]">{fileName || t("Preview File")}</DialogTitle>
        <div className="flex flex-col items-center gap-2 w-full">
          <div className="rounded-sm bg-gray-50 flex gap-2 justify-between items-center w-full p-4">
            <div className="flex gap-4 items-center w-full flex-grow">
              <FileText className={cn("w-6 h-6", fileIconColor)} />
              <div className="flex flex-col">
                <p className="font-medium">{fileName || t("Unnamed file")}</p>
                <p className="text-sm text-muted-foreground">{fileType}</p>
              </div>
            </div>
            <a target="_blank" href={src} download>
              <Button size="icon" variant="ghost" onClick={() => {}}>
                <Download />
              </Button>
            </a>
          </div>
          {canPreview ? (
            <img src={src} className="w-full" />
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 border border-gray-200 w-full rounded-sm aspect-square">
              <FileText className={cn("w-16 h-16", fileIconColor)} />
              <p className="text-lg font-medium">{userFriendlyType}</p>
              <p className="text-muted-foreground">{t("Preview not available")}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
